import React, { useState, useEffect } from "react";
import { io } from "socket.io-client";

import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemContent,
  Button,
  Avatar,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Sheet,
  CircularProgress,
  Alert,
  Input,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  ModalClose,
} from "@mui/joy";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function AdminPanel() {
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [adminProfile, setAdminProfile] = useState(null);
  const [adminProfileImage, setAdminProfileImage] = useState("");
  const [adminProfileError, setAdminProfileError] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [editWorker, setEditWorker] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [role, setRole] = useState("");

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userRole = localStorage.getItem("role");
    if (token && userRole) {
      setIsLoggedIn(true);
      setRole(userRole);
    }
  }, []);

  useEffect(() => {
  const socket = io("https://company-site-backend.onrender.com"); // backend server

  socket.on("newPendingUser", (user) => {
    setPendingUsers((prev) => [...prev, user]);
  });

  socket.on("taskUpdated", (task) => {
    setAssignedTasks((prev) =>
      prev.map((t) => (t._id === task._id ? task : t))
    );
  });

  return () => {
    socket.disconnect();
  };
}, []);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("token");
      const role = localStorage.getItem("role");
      const userId = localStorage.getItem("id");

      console.log("AdminPanel localStorage:", { token, role, userId });

      if (!token) {
        setError("No token found. Please sign in.");
        navigate("/signin");
        return;
      }
      if (role !== "admin") {
        setError("Access denied: You are not an admin.");
        navigate(`/profile/${userId}`);
        return;
      }
      try {
        setLoading(true);
        const responses = await Promise.all([
           axios.get(`${API_URL}/api/admin/pending-users`, {
    headers: { Authorization: `Bearer ${token}` },
  })
            .catch((err) => {
              console.error(
                "Pending users fetch failed:",
                err.response?.data || err.message
              );
              throw new Error(
                `Pending users fetch failed: ${
                  err.response?.data.message || err.message
                }`
              );
            }),
          axios.get(`${API_URL}/api/workers`, {
    headers: { Authorization: `Bearer ${token}` },
  })
            .catch((err) => {
              console.error(
                "Workers fetch failed:",
                err.response?.data || err.message
              );
              throw new Error(
                `Workers fetch failed: ${
                  err.response?.data.message || err.message
                }`
              );
            }),
    axios.get(`${API_URL}/api/inquiries`, {
    headers: { Authorization: `Bearer ${token}` },
  })
            .catch((err) => {
              console.error(
                "Inquiries fetch failed:",
                err.response?.data || err.message
              );
              throw new Error(
                `Inquiries fetch failed: ${
                  err.response?.data.message || err.message
                }`
              );
            }),
         axios.get(`${API_URL}/api/admin/assigned-tasks`, {
    headers: { Authorization: `Bearer ${token}` },
  })
            .catch((err) => {
              console.error(
                "Assigned tasks fetch failed:",
                err.response?.data || err.message
              );
              throw new Error(
                `Assigned tasks fetch failed: ${
                  err.response?.data.message || err.message
                }`
              );
            }),
          axios.get(`${API_URL}/api/profile/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
            .catch((err) => {
              console.error(
                "Profile fetch failed:",
                err.response?.data || err.message
              );
              throw new Error(
                `Profile fetch failed: ${
                  err.response?.data.message || err.message
                }`
              );
            }),
        ]);

        const [
          pendingResponse,
          workersResponse,
          inquiriesResponse,
          tasksResponse,
          profileResponse,
        ] = responses;

        console.log("Pending users:", pendingResponse.data);
        console.log("Workers:", workersResponse.data);
        console.log("Inquiries:", inquiriesResponse.data);
        console.log("Assigned tasks:", tasksResponse.data);
        console.log("Admin profile:", profileResponse.data);
        console.log("Profile image on load:", profileResponse?.data?.image);

        setPendingUsers(pendingResponse.data || []);
        setWorkers(workersResponse.data || []);
        setInquiries(inquiriesResponse.data || []);
        setAssignedTasks(tasksResponse.data || []);
        setAdminProfile(profileResponse.data);
        setAdminProfileImage(profileResponse?.data?.image || "");
      } catch (err) {
        console.error("Fetch data error:", err.message);
        setError(err.message || "Error fetching data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleApproveUser = async (email) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "(`${API_URL}/api/admin/verify-user",
        { email },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPendingUsers(pendingUsers.filter((user) => user.email !== email));
      setError("");
      alert("User approved successfully");
    } catch (err) {
      console.error("Approve user error:", err.response?.data || err.message);
      setError(err.response?.data.message || "Error approving user.");
    }
  };

  const handleRejectUser = async (email) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete("http://localhost:5000/api/admin/pending-users", {
        headers: { Authorization: `Bearer ${token}` },
        data: { email },
      });
      setPendingUsers(pendingUsers.filter((user) => user.email !== email));
      setError("");
      alert("User rejected successfully");
    } catch (err) {
      console.error("Reject user error:", err.response?.data || err.message);
      setError(err.response?.data.message || "Error rejecting user.");
    }
  };

  const handleUpdateInquiryStatus = async (id, status) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `http://localhost:5000/api/admin/inquiry/${id}/status`,
        { status },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setInquiries(
        inquiries.map((inquiry) =>
          inquiry._id === id ? response.data.inquiry : inquiry
        )
      );
      setError("");
      alert("Inquiry status updated successfully");
    } catch (err) {
      console.error("Update inquiry error:", err.response?.data || err.message);
      setError(err.response?.data.message || "Error updating inquiry status.");
    }
  };

  const handleAssignTask = async () => {
    if (!taskTitle || !taskDescription || !selectedWorkerId) {
      setError("Please fill in all task details and select a worker.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "http://localhost:5000/api/admin/assign-task",
        {
          title: taskTitle,
          description: taskDescription,
          workerId: selectedWorkerId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTaskTitle("");
      setTaskDescription("");
      setSelectedWorkerId(null);
      setError("");
      alert("Task assigned successfully");
    } catch (err) {
      console.error("Assign task error:", err.response?.data || err.message);
      setError(err.response?.data.message || "Error assigning task.");
    }
  };

  const handleEditWorker = (worker) => {
    setEditWorker({
      id: worker._id,
      name: worker.name,
      country: worker.country,
      skillTags: worker.skillTags.join(", "),
    });
  };

  const handleUpdateWorker = async () => {
    if (!editWorker.name || !editWorker.country) {
      setError("Name and country are required.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        "http://localhost:5000/api/update-profile",
        {
          userId: editWorker.id,
          name: editWorker.name,
          country: editWorker.country,
          skillTags: editWorker.skillTags.split(",").map((tag) => tag.trim()),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setWorkers(
        workers.map((worker) =>
          worker._id === editWorker.id
            ? {
                ...worker,
                name: editWorker.name,
                country: editWorker.country,
                skillTags: editWorker.skillTags
                  .split(",")
                  .map((tag) => tag.trim()),
              }
            : worker
        )
      );
      setEditWorker(null);
      setError("");
      alert("Worker profile updated successfully");
    } catch (err) {
      console.error("Update worker error:", err.response?.data || err.message);
      setError(err.response?.data.message || "Error updating worker profile.");
    }
  };

  const handleAdminImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("id");
      const response = await axios.post(
        "(`${API_URL}/api/upload-image",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setAdminProfileError("");
      // Re-fetch profile to ensure consistency
      const updatedProfileResponse = await axios.get(
        `${API_URL}/api/profile/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAdminProfile(updatedProfileResponse.data);
      setAdminProfileImage(updatedProfileResponse.data.image || "");
      alert("Profile picture uploaded successfully");
    } catch (err) {
      console.error(
        "Admin image upload error:",
        err.response?.data || err.message
      );
      setAdminProfileError(
        err.response?.data?.message || "Failed to upload image"
      );
    }
  };
  const handleDeleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`http://localhost:5000/api/admin/task/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssignedTasks(assignedTasks.filter((task) => task._id !== taskId));
      setError("");
      alert("Task deleted successfully");
    } catch (err) {
      console.error("Delete task error:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Error deleting task.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("id");
    localStorage.removeItem("role");
    localStorage.removeItem("name");
    localStorage.removeItem("verificationStatus");
    setIsLoggedIn(false);
    setRole("");
    navigate("/");
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          boxShadow: "sm",
        }}
      >
        <Typography level="h6">Mosaic Global Tech</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {isLoggedIn ? (
            <>
              {role === "admin" && (
                <Button
                  onClick={() => navigate("/admin")}
                  variant="plain"
                  color="neutral"
                >
                  Admin Panel
                </Button>
              )}
              {role === "worker" && (
                <Button
                  onClick={() => navigate("/worker")}
                  variant="plain"
                  color="neutral"
                >
                  Worker Panel
                </Button>
              )}
              <Button onClick={handleLogout} color="danger">
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => navigate("/")}
                variant="plain"
                color="neutral"
              >
                Home
              </Button>
              <Button
                onClick={() => navigate("/signup")}
                variant="plain"
                color="neutral"
              >
                Sign Up
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Sheet
        sx={{
          maxWidth: 800,
          mx: "auto",
          my: 4,
          p: 3,
          borderRadius: "md",
          boxShadow: "lg",
        }}
      >
        <Typography
          level="h3"
          sx={{
            mb: 3,
            textAlign: "center",
            fontWeight: "bold",
            color: "primary.main",
          }}
        >
          Admin Panel
        </Typography>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              sx={{ ml: 2 }}
              onClick={() => setError("")}
            >
              Clear
            </Button>
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                mb: 4,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "sm",
              }}
            >
              <Typography level="h5" sx={{ mb: 2 }}>
                Admin Profile
              </Typography>
              {adminProfileError && (
                <Alert color="danger" sx={{ mb: 2 }}>
                  {adminProfileError}
                  <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    sx={{ ml: 2 }}
                    onClick={() => setAdminProfileError("")}
                  >
                    Clear
                  </Button>
                </Alert>
              )}
              {adminProfile ? (
                <>
                  <Avatar
                    src={
                      adminProfileImage ||
                      adminProfile.image ||
                      "https://placehold.co/150x150"
                    }
                    sx={{ width: 100, height: 100, mb: 2, mx: "auto" }}
                    onError={() =>
                      console.log(
                        "Avatar load error:",
                        adminProfileImage || adminProfile.image
                      )
                    }
                  />
                  <Input
                    type="file"
                    onChange={handleAdminImageUpload}
                    sx={{ mb: 2 }}
                    accept="image/*"
                  />
                  <Typography>Name: {adminProfile.name}</Typography>
                  <Typography>Email: {adminProfile.email}</Typography>
                  <Typography>Role: {adminProfile.role}</Typography>
                </>
              ) : (
                <Typography color="danger">
                  Failed to load admin profile.
                </Typography>
              )}
            </Box>

            <Tabs defaultValue={0}>
              <TabList>
                <Tab>Pending Users ({pendingUsers.length})</Tab>
                <Tab>Workers ({workers.length})</Tab>
                <Tab>Inquiries ({inquiries.length})</Tab>
                <Tab>Assigned Tasks ({assignedTasks.length})</Tab>
              </TabList>

              <TabPanel value={0}>
                <Typography level="h5" sx={{ mb: 2 }}>
                  Pending Users
                </Typography>
                {pendingUsers.length > 0 ? (
                  <List>
                    {pendingUsers.map((user) => (
                      <ListItem
                        key={user.email}
                        sx={{
                          mb: 1,
                          p: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "sm",
                        }}
                      >
                        <ListItemContent>
                          <Avatar
                            src={`http://localhost:5000${user}`} // or wherever your backend stores it
                            sx={{ width: 50, height: 50, mr: 2 }}
                          />
                          <Typography level="body1">{user.name}</Typography>
                          <Typography level="body2">{user.email}</Typography>
                          <Typography level="body2">
                            Country: {user.country}
                          </Typography>
                          <Typography level="body2">
                            Telegram: {user.telegram || "N/A"}
                          </Typography>
                          <Typography level="body2">
                            Twitter: {user.twitter || "N/A"}
                          </Typography>
                          <Typography level="body2">
                            Phone: {user.phone || "N/A"}
                          </Typography>

                          <Typography level="body2">
                            Skills: {user.skillTags.join(", ")}
                          </Typography>
                        </ListItemContent>
                        <Button
                          color="success"
                          sx={{ mr: 1 }}
                          onClick={() => handleApproveUser(user.email)}
                        >
                          Approve
                        </Button>
                        <Button
                          color="danger"
                          onClick={() => handleRejectUser(user.email)}
                        >
                          Reject
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography>No pending users to verify.</Typography>
                )}
              </TabPanel>

              <TabPanel value={1}>
                <Typography level="h5" sx={{ mb: 2 }}>
                  Workers
                </Typography>

                {workers.length > 0 ? (
                  <List>
                    {workers.map((worker) => (
                      <ListItem
                        key={worker._id}
                        sx={{
                          mb: 1,
                          p: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "sm",
                        }}
                      >
                        <ListItemContent>
                          <Avatar
                            src={`http://localhost:5000${worker.image}`} // or wherever your backend stores it
                            sx={{ width: 50, height: 50, mr: 2 }}
                          />
                          <Typography level="body1">{worker.name}</Typography>
                          <Typography level="body2">
                            Email: {worker.email}
                          </Typography>
                          <Typography level="body2">
                            Skills: {worker.skillTags.join(", ")}
                          </Typography>
                          <Typography level="body2">
                            Country: {worker.country}
                          </Typography>
                          <Typography level="body2">
                            Status: {worker.verificationStatus}
                          </Typography>
                          <Typography level="body2">
                            Telegram: {worker.telegram || "N/A"}
                          </Typography>
                          <Typography level="body2">
                            Twitter: {worker.twitter || "N/A"}
                          </Typography>
                          <Typography level="body2">
                            Phone: {worker.phone || "N/A"}
                          </Typography>
                          <Box sx={{ mt: 2 }}>
                            <Typography level="body2" sx={{ mb: 1 }}>
                              Assign Task
                            </Typography>
                            <FormControl sx={{ mb: 1 }}>
                              <FormLabel>Task Title</FormLabel>
                              <Input
                                placeholder="Enter task title"
                                value={
                                  selectedWorkerId === worker._id
                                    ? taskTitle
                                    : ""
                                }
                                onChange={(e) => {
                                  setSelectedWorkerId(worker._id);
                                  setTaskTitle(e.target.value);
                                }}
                              />
                            </FormControl>
                            <FormControl sx={{ mb: 1 }}>
                              <FormLabel>Task Description</FormLabel>
                              <Input
                                placeholder="Enter task description"
                                value={
                                  selectedWorkerId === worker._id
                                    ? taskDescription
                                    : ""
                                }
                                onChange={(e) => {
                                  setSelectedWorkerId(worker._id);
                                  setTaskDescription(e.target.value);
                                }}
                              />
                            </FormControl>
                            <Button
                              color="primary"
                              onClick={handleAssignTask}
                              disabled={selectedWorkerId !== worker._id}
                            >
                              Assign Task
                            </Button>
                          </Box>
                        </ListItemContent>
                        <Button
                          color="neutral"
                          variant="outlined"
                          sx={{ ml: 1 }}
                          onClick={() => handleEditWorker(worker)}
                        >
                          Edit Profile
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography>No verified workers found.</Typography>
                )}
              </TabPanel>

              <TabPanel value={2}>
                <Typography level="h5" sx={{ mb: 2 }}>
                  Inquiries
                </Typography>
                {inquiries.length > 0 ? (
                  <List>
                    {inquiries.map((inquiry) => (
                      <ListItem
                        key={inquiry._id}
                        sx={{
                          mb: 1,
                          p: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "sm",
                        }}
                      >
                        <ListItemContent>
                          <Typography level="body1">
                            {inquiry.name} ({inquiry.email})
                          </Typography>
                          <Typography level="body2">
                            Message: {inquiry.message}
                          </Typography>
                          <Typography level="body2">
                            Status: {inquiry.status}
                          </Typography>
                          <Typography level="body2">
                            Received:{" "}
                            {new Date(inquiry.createdAt).toLocaleString()}
                          </Typography>
                        </ListItemContent>
                        {inquiry.status === "pending" && (
                          <Button
                            color="primary"
                            onClick={() =>
                              handleUpdateInquiryStatus(inquiry._id, "resolved")
                            }
                          >
                            Mark as Resolved
                          </Button>
                        )}
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography>No inquiries found.</Typography>
                )}
              </TabPanel>

              <TabPanel value={3}>
                <Typography level="h5" sx={{ mb: 2 }}>
                  Assigned Tasks
                </Typography>
                {assignedTasks.length > 0 ? (
                  <List>
                    {assignedTasks.map((task) => (
                      <ListItem
                        key={task._id}
                        sx={{
                          mb: 1,
                          p: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "sm",
                        }}
                      >
                        <ListItemContent>
                          <Typography level="h6">{task.title}</Typography>
                          <Typography>{task.description}</Typography>
                          <Typography level="body2">
                            Assigned To: {task.assignedTo?.name} (
                            {task.assignedTo?.email})
                          </Typography>
                          <Typography level="body2">
                            Status: {task.status}
                          </Typography>
                          <Typography level="body2">
                            Assigned On:{" "}
                            {task.createdAt
                              ? new Date(task.createdAt).toLocaleString()
                              : "N/A"}
                          </Typography>
                          {task.comments && task.comments.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography level="body2">Comments:</Typography>
                              <List>
                                {task.comments.map((comment, index) => (
                                  <ListItem key={index}>
                                    <Typography level="body2">
                                      {comment.text} -{" "}
                                      {new Date(
                                        comment.createdAt
                                      ).toLocaleString()}
                                    </Typography>
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                        </ListItemContent>
                        <Button
                          color="danger"
                          onClick={() => handleDeleteTask(task._id)}
                        >
                          Delete
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography>No tasks assigned by you.</Typography>
                )}
              </TabPanel>
            </Tabs>
          </>
        )}

        {editWorker && (
          <Modal open={!!editWorker} onClose={() => setEditWorker(null)}>
            <ModalDialog>
              <ModalClose />
              <Typography level="h5" sx={{ mb: 2 }}>
                Edit Worker Profile
              </Typography>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Name</FormLabel>
                <Input
                  value={editWorker.name}
                  onChange={(e) =>
                    setEditWorker({ ...editWorker, name: e.target.value })
                  }
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Country</FormLabel>
                <Input
                  value={editWorker.country}
                  onChange={(e) =>
                    setEditWorker({ ...editWorker, country: e.target.value })
                  }
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Skill Tags (comma-separated)</FormLabel>
                <Input
                  value={editWorker.skillTags}
                  onChange={(e) =>
                    setEditWorker({ ...editWorker, skillTags: e.target.value })
                  }
                />
              </FormControl>
              <Button color="primary" onClick={handleUpdateWorker}>
                Save Changes
              </Button>
            </ModalDialog>
          </Modal>
        )}
      </Sheet>
    </>
  );
}
